import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Breadcrumbs, Button, Divider, showToast } from '@diamond/shared/ui';
import { AdminLayout, ExcelFileDropzone } from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { downloadBlob, getApiErrorMessage } from '@diamond/sol-admin/utils';
import { CloudDownloadOutlined, UploadFileOutlined } from '@mui/icons-material';
import { useCallback, useRef, useState } from 'react';

import {
  EXPORT_FILENAME,
  exportEstimatedWeightProduct,
  importEstimatedWeightProduct,
} from '../api';

const BREADCRUMBS = [
  {
    title: 'Beranda',
    link: '/',
  },
  {
    title: 'Estimated Weight Product',
    link: '/estimated-weight-product',
  },
];

export function EstimatedWeightProductPage() {
  const toast = useToast();
  const session = useAuthStore();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();

  const [file, setFile] = useState<File | null>(null);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDropzoneError = useCallback(
    (message: string) => {
      showToast(toast, 'error', message);
    },
    [toast]
  );

  const handleFileChange = useCallback(
    (nextFile: File | null) => {
      if (isConfirmOpen || isUploading) {
        return;
      }
      setFile(nextFile);
    },
    [isConfirmOpen, isUploading]
  );

  const handleDownload = useCallback(async () => {
    if (isDownloading || isUploading) {
      return;
    }

    setIsDownloading(true);

    try {
      const blob = await exportEstimatedWeightProduct(session.access_token);
      downloadBlob(blob, EXPORT_FILENAME);
      showToast(toast, 'success', 'Konfigurasi saat ini berhasil diunduh');
    } catch (error) {
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal mengunduh konfigurasi saat ini')
      );
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, isUploading, session.access_token, toast]);

  const handleOpenConfirm = useCallback(() => {
    if (!file || isUploading || isDownloading) {
      return;
    }

    setPendingUploadFile(file);
    onConfirmOpen();
  }, [file, isDownloading, isUploading, onConfirmOpen]);

  const handleCancelConfirm = useCallback(() => {
    if (isUploading) {
      return;
    }

    setPendingUploadFile(null);
    onConfirmClose();
  }, [isUploading, onConfirmClose]);

  const handleConfirmUpload = useCallback(async () => {
    if (!pendingUploadFile || isUploading) {
      return;
    }

    setIsUploading(true);

    try {
      const response = await importEstimatedWeightProduct(
        session.access_token,
        pendingUploadFile
      );
      const importedCount = response.data?.imported_count ?? 0;
      const message =
        importedCount > 0
          ? `${response.message} (${importedCount} baris berhasil diimpor)`
          : response.message;

      showToast(toast, 'success', message || 'File berhasil di-upload');
      setFile(null);
      setPendingUploadFile(null);
      onConfirmClose();
    } catch (error) {
      showToast(
        toast,
        'error',
        getApiErrorMessage(
          error,
          'Gagal meng-upload file Product Payment Configuration'
        )
      );
      setPendingUploadFile(null);
      onConfirmClose();
    } finally {
      setIsUploading(false);
    }
  }, [
    isUploading,
    onConfirmClose,
    pendingUploadFile,
    session.access_token,
    toast,
  ]);

  const isDropzoneDisabled =
    isDownloading || isUploading || isConfirmOpen;

  return (
    <AdminLayout>
      <Box pb={4}>
        <Breadcrumbs links={BREADCRUMBS} useRouterDom />
      </Box>

      <Flex justify="space-between" align="center" gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" as="b" color="gray.800">
            Estimated Weight Product Config
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            Upload konfigurasi metode pembayaran produk berdasarkan distribution
            channel melalui file Excel.
          </Text>
        </Box>

        <HStack spacing={3}>
          <Button
            variant="outline"
            onClick={handleDownload}
            isLoading={isDownloading}
            isDisabled={isUploading}
          >
            <HStack spacing={2}>
              <Icon as={CloudDownloadOutlined} boxSize={4} />
              <Text>Export Current Configuration</Text>
            </HStack>
          </Button>
          <Button
            variant="solid"
            onClick={handleOpenConfirm}
            isLoading={isUploading}
            isDisabled={!file || isDownloading}
          >
            <HStack spacing={2}>
              <Icon as={UploadFileOutlined} boxSize={4} />
              <Text>Upload File</Text>
            </HStack>
          </Button>
        </HStack>
      </Flex>

      <Stack pt={4} pb={8}>
        <Divider />
      </Stack>

      <Stack
        spacing={4}
        border="1px"
        borderRadius="md"
        borderColor="gray.200"
        bg="white"
        px={{ base: 3, md: 5 }}
        py={{ base: 4, md: 5 }}
        boxShadow="sm"
      >
        <Box
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="gray.50"
        >
          <Stack spacing={2}>
            <Text fontSize="lg" as="b" color="gray.800">
              Upload Product Payment Configuration
            </Text>
            <Text fontSize="sm" color="gray.700">
              Upload file Excel untuk konfigurasi metode pembayaran produk
              berdasarkan distribution channel.
            </Text>
            <Text fontSize="sm" color="gray.600">
              Kolom wajib: distribution_channel, product_item_code,
              payment_method.
            </Text>
            <Text fontSize="sm" color="gray.600">
              Nilai payment_method yang valid: cash_on_delivery atau
              bank_transfer. Gunakan dua baris bila kedua metode diizinkan.
            </Text>
            <HStack spacing={2} flexWrap="wrap" pt={1}>
              <Badge colorScheme="purple" px={2} py={1} borderRadius="md">
                Format xlsx
              </Badge>
              <Badge colorScheme="orange" px={2} py={1} borderRadius="md">
                Max 2MB
              </Badge>
              <Badge colorScheme="teal" px={2} py={1} borderRadius="md">
                1 file per upload
              </Badge>
            </HStack>
          </Stack>
        </Box>

        <Alert
          status="warning"
          borderRadius="md"
          alignItems="flex-start"
          bg="#FFF7ED"
          border="1px solid"
          borderColor="#FED7AA"
          px={4}
          py={3}
        >
          <AlertIcon mt={0.5} color="#C2410C" />
          <Text
            fontSize="sm"
            color="#9A3412"
            lineHeight="1.5"
            fontWeight="medium"
          >
            Perhatian: File ini merupakan snapshot penuh. Konfigurasi aktif yang
            tidak terdapat dalam file akan dinonaktifkan. Export konfigurasi
            saat ini sebagai backup sebelum melakukan upload.
          </Text>
        </Alert>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            File Product Payment Configuration
          </FormLabel>
          <ExcelFileDropzone
            file={file}
            onFileChange={handleFileChange}
            onFileRejected={handleDropzoneError}
            isDisabled={isDropzoneDisabled}
            isUploading={isUploading}
          />
        </FormControl>
      </Stack>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={handleCancelConfirm}
        closeOnOverlayClick={!isUploading}
        closeOnEsc={!isUploading}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Upload konfigurasi?
            </AlertDialogHeader>

            <AlertDialogBody>
              File ini akan menggantikan konfigurasi aktif. Konfigurasi yang
              tidak ada di file akan dinonaktifkan. Pastikan Anda sudah export
              konfigurasi saat ini sebagai backup.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={handleCancelConfirm}
                variant="outline"
                isDisabled={isUploading}
              >
                Batal
              </Button>
              <Button
                ml={3}
                onClick={handleConfirmUpload}
                isLoading={isUploading}
                isDisabled={isUploading}
              >
                Ya, Upload File
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </AdminLayout>
  );
}
